import React, { useRef } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import {
    FigureModalSource,
    inferFigureTextFromPath,
} from "components/organisms/manuscript/overleaf/visual/figure-modal-context";
import { handleImagePaste, isAllowedImageType } from "components/organisms/manuscript/overleaf/visual/paste-image";

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(15, 23, 42, 0.28);
    backdrop-filter: blur(2px);
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    width: min(760px, 100%);
    max-height: min(720px, calc(100vh - 96px));
    border: 1px solid #d8e1ec;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
`;

const Title = styled.div`
    color: #17314a;
    font-size: 16px;
    font-weight: 700;
`;

const Body = styled.div`
    display: grid;
    gap: 16px;
    padding: 18px;
    overflow: auto;
`;

const Field = styled.div`
    display: grid;
    gap: 8px;
`;

const Label = styled.div`
    color: #42576b;
    font-size: 12px;
    font-weight: 700;
`;

const HeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const Input = styled.input`
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    background: #ffffff;
    color: #24384d;
    font-size: 13px;
    outline: none;

    &:focus {
        border-color: #0f8f6f;
        box-shadow: 0 0 0 3px rgba(15, 143, 111, 0.12);
    }
`;

const Value = styled.div`
    color: #5c7187;
    font-size: 12px;
    line-height: 1.5;
`;

const Select = styled.select`
    width: 100%;
    height: 38px;
    padding: 0 10px;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    background: #ffffff;
    color: #24384d;
    font-size: 13px;
    outline: none;

    &:focus {
        border-color: #0f8f6f;
        box-shadow: 0 0 0 3px rgba(15, 143, 111, 0.12);
    }
`;

const ReadonlyInput = styled(Input)`
    background: #f8fafc;
    color: #536981;
`;

const UploadDropzone = styled.div`
    display: grid;
    gap: 6px;
    padding: 18px;
    border: 1px dashed #b9c8d8;
    border-radius: 10px;
    background: #f8fbfd;
    text-align: center;
`;

const UploadTitle = styled.div`
    color: #24384d;
    font-size: 13px;
    font-weight: 700;
`;

const UploadSubtitle = styled.div`
    color: #698096;
    font-size: 12px;
`;

const UploadCurrent = styled.div`
    color: #0f8f6f;
    font-size: 12px;
    font-weight: 700;
    word-break: break-all;
`;

const UploadActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const CheckboxRow = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #24384d;
    font-size: 12px;
    font-weight: 700;
`;

const OptionsGroup = styled.div`
    display: grid;
    gap: 10px;
`;

const OptionText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #24384d;
    font-size: 12px;
    font-weight: 700;
`;

const OptionHint = styled.span`
    color: #6c8196;
    font-size: 11px;
    font-weight: 500;
`;

const WidthGroup = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
`;

const WidthButton = styled.button`
    height: 34px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$active ? "#0f8f6f" : "#d9e2ec")};
    border-radius: 8px;
    background: ${(props) => (props.$active ? "rgba(15, 143, 111, 0.08)" : "#ffffff")};
    color: ${(props) => (props.$active ? "#0f8f6f" : "#42576b")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

const HelpPanel = styled.div`
    display: grid;
    gap: 8px;
    padding: 14px 16px;
    border: 1px solid #d9e2ec;
    border-radius: 10px;
    background: #f8fbfd;
    color: #536981;
    font-size: 12px;
    line-height: 1.5;
`;

const Section = styled.div`
    display: grid;
    gap: 12px;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 16px 18px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
`;

const FooterActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    padding: 0 14px;
    border: 1px solid ${(props) => (props.$primary ? "#12805f" : "#d9e2ec")};
    border-radius: 8px;
    background: ${(props) => (props.$primary ? "#138a6d" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#42576b")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function ManuscriptFigureModal({
    activeFigureData,
    figureDraft,
    figureModalSource,
    isFigureUploadPending,
    isImageBinary,
    projectFigureFiles,
    projectFolders = [],
    onClose,
    onDelete,
    onApply,
    onDraftChange,
    onUploadFile,
}) {
    const fileInputRef = useRef(null);
    const widthPresets = [
        { value: "0.25", label: "1/4" },
        { value: "0.5", label: "1/2" },
        { value: "0.75", label: "3/4" },
        { value: "1", label: "Full" },
    ];

    if (!figureDraft) {
        return null;
    }

    const isEditMode = figureModalSource === FigureModalSource.EDIT_FIGURE;
    const titleBySource = {
        [FigureModalSource.FILE_UPLOAD]: "컴퓨터에서 업로드",
        [FigureModalSource.FILE_TREE]: "프로젝트 파일에서 삽입",
        [FigureModalSource.FROM_URL]: "URL에서 삽입",
        [FigureModalSource.EDIT_FIGURE]: "그림 편집",
    };

    const title = titleBySource[figureModalSource] || "그림 삽입";
    const selectedWidth = String(figureDraft?.width ?? "");
    const selectedFolderId = figureDraft?.uploadParentId == null ? "" : String(figureDraft.uploadParentId);
    const savedName =
        String(figureDraft?.path || "")
            .split("/")
            .pop() || "";
    const savedFolderPath = String(figureDraft?.path || "").includes("/")
        ? String(figureDraft.path).split("/").slice(0, -1).join("/")
        : "";
    const uploadHelpText =
        typeof navigator === "undefined" || !navigator.clipboard?.read
            ? "드롭, 파일 선택, 에디터에서 붙여넣기를 지원합니다."
            : "드롭, 파일 선택, 클립보드 붙여넣기를 지원합니다.";

    const updateDraft = (patch) => {
        onDraftChange((current) => ({
            ...current,
            ...patch,
        }));
    };

    const handlePathChange = (event) => {
        const nextPath = event.target.value;
        const inferred =
            figureModalSource === FigureModalSource.FROM_URL || figureModalSource === FigureModalSource.FILE_TREE
                ? inferFigureTextFromPath(nextPath, figureDraft)
                : {};

        updateDraft({
            path: nextPath,
            ...inferred,
        });
    };

    const handleProjectFilePick = (path) => {
        updateDraft({
            path,
            ...inferFigureTextFromPath(path, figureDraft),
        });
    };

    const uploadImageFile = async (file) => {
        if (isFigureUploadPending || !file || !isAllowedImageType(file.type)) {
            return false;
        }

        await onUploadFile?.(file, figureDraft?.uploadParentId || undefined);
        return true;
    };

    const handleUploadDrop = async (event) => {
        event.preventDefault();
        const file = event.dataTransfer?.files?.[0];
        await uploadImageFile(file);
    };

    const handleUploadPaste = async (event) => {
        const file = event.clipboardData?.files?.[0];
        if (!file || event.clipboardData?.types?.includes("text/plain")) {
            return;
        }

        const didUpload = await uploadImageFile(file);
        if (didUpload) {
            event.preventDefault();
        }
    };

    const handleClipboardUpload = async () => {
        if (isFigureUploadPending || typeof navigator === "undefined" || !navigator.clipboard?.read) {
            return;
        }

        await handleImagePaste();
    };

    const renderSourceBody = () => {
        if (figureModalSource === FigureModalSource.FILE_UPLOAD) {
            return (
                <Section>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">파일 업로드</Label>
                        <UploadDropzone
                            className="manuscript-figure-modal-upload-dropzone"
                            tabIndex={0}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={handleUploadDrop}
                            onPaste={handleUploadPaste}
                        >
                            <UploadTitle className="manuscript-figure-modal-upload-title">
                                파일을 드롭하거나 선택해서 업로드
                            </UploadTitle>
                            <UploadSubtitle className="manuscript-figure-modal-upload-subtitle">
                                PNG, JPG, SVG, PDF, PS 형식을 지원합니다. {uploadHelpText}
                            </UploadSubtitle>
                            {figureDraft?.path ? (
                                <UploadCurrent className="manuscript-figure-modal-upload-current">
                                    {figureDraft.path}
                                </UploadCurrent>
                            ) : null}
                        </UploadDropzone>
                        <UploadActions className="manuscript-figure-modal-upload-actions">
                            <Button
                                type="button"
                                className="manuscript-figure-modal-button"
                                onClick={handleClipboardUpload}
                                disabled={
                                    isFigureUploadPending ||
                                    typeof navigator === "undefined" ||
                                    !navigator.clipboard?.read
                                }
                                title={
                                    typeof navigator === "undefined" || !navigator.clipboard?.read
                                        ? "이 브라우저에서는 클립보드 읽기를 지원하지 않습니다."
                                        : undefined
                                }
                            >
                                클립보드 이미지 붙여넣기
                            </Button>
                            <Button
                                type="button"
                                className="manuscript-figure-modal-button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isFigureUploadPending}
                            >
                                {isFigureUploadPending ? "업로드 중..." : "파일 선택"}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept="image/*,.pdf,.ps,.eps"
                                onChange={async (event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                        await uploadImageFile(file);
                                    }
                                    event.target.value = "";
                                }}
                            />
                        </UploadActions>
                    </Field>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">저장될 이름</Label>
                        <ReadonlyInput value={savedName} placeholder="파일을 업로드해주세요" readOnly />
                    </Field>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">저장될 위치</Label>
                        <Select
                            value={selectedFolderId}
                            disabled={Boolean(figureDraft?.path)}
                            onChange={(event) =>
                                updateDraft({
                                    uploadParentId: event.target.value ? Number(event.target.value) : null,
                                })
                            }
                        >
                            <option value="">폴더 선택 안함</option>
                            {(projectFolders || []).map((folder) => (
                                <option key={folder.id} value={String(folder.id)}>
                                    {folder.path}
                                </option>
                            ))}
                        </Select>
                        {savedFolderPath ? (
                            <Value className="manuscript-figure-modal-value">현재 저장 위치: {savedFolderPath}</Value>
                        ) : null}
                    </Field>
                </Section>
            );
        }

        if (figureModalSource === FigureModalSource.FROM_URL) {
            return (
                <Section>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">이미지 URL / 경로</Label>
                        <Input
                            className="manuscript-figure-modal-input"
                            value={figureDraft?.path || ""}
                            onChange={handlePathChange}
                            placeholder="URL 또는 프로젝트 내 이미지 경로를 입력합니다."
                        />
                    </Field>
                </Section>
            );
        }

        if (figureModalSource === FigureModalSource.FILE_TREE) {
            return (
                <Section>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">파일 선택</Label>
                        <Select
                            className="manuscript-figure-modal-file-select"
                            value={figureDraft?.path || ""}
                            onChange={(event) => handleProjectFilePick(event.target.value)}
                        >
                            <option value="">파일을 선택해주세요</option>
                            {(projectFigureFiles || []).map((file) => (
                                <option key={file.id || file.path} value={file.path}>
                                    {file.name || file.path}
                                </option>
                            ))}
                        </Select>
                        {figureModalSource === FigureModalSource.FILE_TREE && !isImageBinary ? (
                            <Value className="manuscript-figure-modal-value">현재 선택된 이미지 파일이 없습니다.</Value>
                        ) : null}
                    </Field>
                </Section>
            );
        }

        if (figureModalSource === FigureModalSource.EDIT_FIGURE) {
            return (
                <Section>
                    <Field className="manuscript-figure-modal-field">
                        <Label className="manuscript-figure-modal-label">이미지 URL / 경로</Label>
                        <Input
                            className="manuscript-figure-modal-input"
                            value={figureDraft?.path || ""}
                            onChange={handlePathChange}
                            placeholder="URL 또는 프로젝트 내 이미지 경로를 입력합니다."
                        />
                    </Field>
                </Section>
            );
        }

        return null;
    };

    return (
        <Overlay
            className="manuscript-figure-modal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <Card className="manuscript-figure-modal-card">
                <Header className="manuscript-figure-modal-header">
                    <Title className="manuscript-figure-modal-title">{title}</Title>
                    <HeaderActions>
                        <Button
                            type="button"
                            className="manuscript-figure-modal-button"
                            onClick={() => updateDraft({ helpShown: !figureDraft?.helpShown })}
                        >
                            {figureDraft?.helpShown ? "뒤로" : "도움말"}
                        </Button>
                        <Button
                            type="button"
                            className="manuscript-figure-modal-button"
                            onClick={onClose}
                            aria-label="닫기"
                        >
                            <Icon name={"x"} size={"10px"} color={"#42576b"} />
                        </Button>
                    </HeaderActions>
                </Header>
                <Body className="manuscript-figure-modal-body">
                    {figureDraft?.helpShown ? (
                        <HelpPanel>
                            <div>이미지 소스를 선택한 뒤 그림을 에디터에 삽입합니다.</div>
                            <div>
                                업로드는 붙여넣기, 드롭, 파일 선택을 지원하며 업로드한 파일 경로를 바로 사용할 수
                                있습니다.
                            </div>
                            <div>너비는 문서의 본문 폭을 기준으로 적용됩니다.</div>
                            <div>캡션과 라벨 옵션은 표준 LaTeX Placeholder 를 삽입합니다.</div>
                        </HelpPanel>
                    ) : null}

                    {!figureDraft?.helpShown ? (
                        <>
                            {renderSourceBody()}

                            <Field className="manuscript-figure-modal-field">
                                <Label className="manuscript-figure-modal-label">이미지 너비</Label>
                                <WidthGroup>
                                    {widthPresets.map((preset) => (
                                        <WidthButton
                                            key={preset.value}
                                            type="button"
                                            $active={selectedWidth === preset.value}
                                            onClick={() => updateDraft({ width: preset.value })}
                                        >
                                            {preset.label}
                                        </WidthButton>
                                    ))}
                                </WidthGroup>
                            </Field>

                            <Field className="manuscript-figure-modal-field">
                                <Label className="manuscript-figure-modal-label">옵션</Label>
                                <OptionsGroup>
                                    <CheckboxRow className="manuscript-figure-modal-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(figureDraft?.includeCaption)}
                                            onChange={(event) => updateDraft({ includeCaption: event.target.checked })}
                                        />
                                        <OptionText>
                                            캡션
                                            <OptionHint>{"그림에 표준 \\caption{...} 줄을 삽입합니다."}</OptionHint>
                                        </OptionText>
                                    </CheckboxRow>
                                    <CheckboxRow className="manuscript-figure-modal-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(figureDraft?.includeLabel)}
                                            onChange={(event) => updateDraft({ includeLabel: event.target.checked })}
                                        />
                                        <OptionText>
                                            라벨
                                            <OptionHint>문서 내 상호 참조에 사용됩니다.</OptionHint>
                                        </OptionText>
                                    </CheckboxRow>
                                </OptionsGroup>
                            </Field>
                        </>
                    ) : null}
                </Body>

                <Footer className="manuscript-figure-modal-footer">
                    {activeFigureData ? (
                        <Button type="button" className="manuscript-figure-modal-button" onClick={onDelete}>
                            삭제
                        </Button>
                    ) : null}
                    <FooterActions>
                        <Button type="button" className="manuscript-figure-modal-button" onClick={onClose}>
                            취소
                        </Button>
                        <Button
                            type="button"
                            className="manuscript-figure-modal-button manuscript-figure-modal-button-primary"
                            $primary
                            onClick={onApply}
                            disabled={
                                figureDraft?.helpShown ||
                                isFigureUploadPending ||
                                (!isEditMode && !String(figureDraft?.path || "").trim())
                            }
                        >
                            {isEditMode ? "완료" : "그림 삽입"}
                        </Button>
                    </FooterActions>
                </Footer>
            </Card>
        </Overlay>
    );
}

export default ManuscriptFigureModal;
